import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerChild } from '../../ui/animations';
import { CAMPAIGN_TEMPLATES } from '../../game/data/marketingCampaigns';
import { MAX_TOURS_PER_MONTH, TOUR_FEE } from '../../game/systems/marketing';

// ── Newsroom palette (CSS-variable compatible) ──────────────────────────────
const PAPER  = '#e8e2d9';
const AMBER  = '#d97706';
const RUST   = '#b45309';
const FOREST = '#166534';
const DANGER = '#991b1b';

function SectionHead({ children, accent }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: accent || AMBER, borderBottom: `1px solid ${accent || AMBER}33`,
      paddingBottom: 4, marginBottom: 10, marginTop: 18,
    }}>
      {children}
    </div>
  );
}

function Meter({ label, value, max, color, format }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, marginBottom: 4 }}>{format ? format(value) : value}</div>
      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ── Campaign Card — "newspaper clipping" style ──────────────────────────────
function CampaignCard({ template, canLaunch, canAfford, onLaunch }) {
  const riskLabel = template.backlashRisk >= 0.35 ? 'HIGH' : template.backlashRisk >= 0.2 ? 'MED' : 'LOW';
  const riskColor = template.backlashRisk >= 0.35 ? DANGER : template.backlashRisk >= 0.2 ? AMBER : FOREST;

  return (
    <motion.div variants={staggerChild} style={{
      background: '#1c1a16', border: `1px solid ${AMBER}33`, borderRadius: 6,
      padding: '12px 14px', position: 'relative', overflow: 'hidden',
    }}>
      {/* Subtle newsprint texture overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.03, background: `repeating-linear-gradient(0deg, ${PAPER} 0px, transparent 1px, transparent 3px)`, pointerEvents: 'none' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: PAPER, marginBottom: 2 }}>{template.name}</div>
          <div style={{ fontSize: 11, color: '#a09882', lineHeight: 1.4 }}>{template.description}</div>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: riskColor, border: `1px solid ${riskColor}`, borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap', marginLeft: 8 }}>
          {riskLabel} RISK
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, fontSize: 11, marginBottom: 10 }}>
        <div><span style={{ color: '#a09882' }}>Cost</span><br /><b style={{ color: AMBER }}>${template.cost.toLocaleString()}</b></div>
        <div><span style={{ color: '#a09882' }}>Days</span><br /><b>{template.durationDays}</b></div>
        <div><span style={{ color: '#a09882' }}>Brand</span><br /><b style={{ color: FOREST }}>+{template.brandDelta}</b></div>
        <div><span style={{ color: '#a09882' }}>Demand</span><br /><b style={{ color: FOREST }}>+{(template.demandDelta * 100).toFixed(0)}%</b></div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: '#706856' }}>
          Quality floor: {template.qualityFloor} | Channel: {template.channelAffinity} | Sat cost: +{template.saturationCost}
        </span>
        <button
          className="btn btn-primary btn-sm"
          style={{ background: canLaunch && canAfford ? RUST : undefined, borderColor: canLaunch && canAfford ? AMBER : undefined, opacity: canLaunch && canAfford ? 1 : 0.4 }}
          disabled={!canLaunch || !canAfford}
          onClick={onLaunch}
        >
          {!canAfford ? 'Not enough cash' : 'Launch'}
        </button>
      </div>
    </motion.div>
  );
}

// ── Active Campaign Row ─────────────────────────────────────────────────────
function ActiveCampaignRow({ campaign, onCancel }) {
  const template = CAMPAIGN_TEMPLATES.find(t => t.id === campaign.templateId) || {};
  const total = campaign.totalDays || template.durationDays || campaign.daysRemaining;
  const elapsed = total - campaign.daysRemaining;
  const pct = Math.round((elapsed / total) * 100);

  return (
    <div style={{ background: '#1c1a16', border: `1px solid ${AMBER}22`, borderRadius: 6, padding: '10px 12px', marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: PAPER }}>{campaign.name || template.name}</span>
        <span style={{ fontSize: 11, color: '#a09882' }}>{campaign.daysRemaining}d remaining</span>
      </div>
      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: AMBER, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={onCancel}>
        Cancel (50% refund)
      </button>
    </div>
  );
}

// ── Main Screen ─────────────────────────────────────────────────────────────
export function MarketingScreen({ state, dispatch }) {
  const mkt = state.marketing || {};
  const brand = state.brandValue ?? 0;
  const demand = state.demandModifier ?? 1.0;
  const campaigns = mkt.activeCampaigns || [];
  const slots = mkt.availableCampaignSlots || [];
  const tours = mkt.scheduledTours || [];
  const crises = mkt.prCrises || [];
  const saturation = mkt.socialSaturation || 0;
  const hasD4 = state.unlockedTech?.includes('D4') || false;
  const hasD2 = state.unlockedTech?.includes('D2') || false;

  const [budgetInput, setBudgetInput] = useState(mkt.weeklyBudget || 0);

  const monthKey = `${Math.floor((state.gameDay || 0) / 30)}`;
  const tourCountThisMonth = mkt.lastTourMonthKey === monthKey ? (mkt.monthlyTourCount || 0) : 0;
  const canScheduleTour = hasD4 && tourCountThisMonth < MAX_TOURS_PER_MONTH && state.cash >= TOUR_FEE;

  function handleSetBudget() {
    const amount = Math.max(0, Math.round(budgetInput));
    dispatch({ type: 'SET_MARKETING_BUDGET', payload: { amount } });
  }

  function handleLaunch(templateId) {
    dispatch({ type: 'LAUNCH_CAMPAIGN', payload: { templateId } });
  }

  function handleCancel(campaignId) {
    dispatch({ type: 'CANCEL_CAMPAIGN', payload: { campaignId } });
  }

  function handleScheduleTour() {
    dispatch({ type: 'SCHEDULE_TOUR', payload: {} });
  }

  function handleCancelTour(tourId) {
    dispatch({ type: 'CANCEL_TOUR', payload: { tourId } });
  }

  function handlePRResponse(crisisId, response) {
    dispatch({ type: 'RESPOND_TO_PR', payload: { crisisId, response } });
  }

  // History for the sidebar
  const history = (mkt.completedCampaignHistory || []).slice(-8).reverse();

  return (
    <div className="screen" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 14 }}>

      {/* ═══ LEFT — Main workspace ═══ */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}>

        {/* ── Header strip: brand + demand meters ── */}
        <div style={{
          display: 'flex', gap: 20, padding: '14px 16px', marginBottom: 4,
          background: '#141210', borderRadius: 8, border: `1px solid ${AMBER}22`,
        }}>
          <Meter label="Brand Value" value={brand} max={100} color={AMBER} />
          <Meter label="Demand" value={demand} max={1.30} color={FOREST} format={v => `x${v.toFixed(2)}`} />
          <Meter label="Saturation" value={saturation} max={10} color={saturation > 5 ? DANGER : AMBER} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Budget / wk</div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number"
                min={0} max={5000} step={100}
                value={budgetInput}
                onChange={e => setBudgetInput(Number(e.target.value))}
                style={{
                  width: 70, padding: '4px 6px', fontSize: 13, fontWeight: 700,
                  background: '#1c1a16', border: `1px solid ${AMBER}44`, borderRadius: 4,
                  color: AMBER, textAlign: 'right',
                }}
              />
              <button
                className="btn btn-secondary btn-sm"
                style={{ fontSize: 10, padding: '4px 8px' }}
                disabled={!hasD4}
                onClick={handleSetBudget}
              >
                Set
              </button>
            </div>
            {!hasD4 && <div style={{ fontSize: 10, color: DANGER, marginTop: 3 }}>Requires D4</div>}
          </div>
        </div>

        {/* ── Campaign Board ── */}
        <SectionHead>Campaign Board — Weekly Offers</SectionHead>
        {!hasD2 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>
            Unlock <b>D2 — Brand Builder</b> research to access campaigns.
          </div>
        )}
        {hasD2 && slots.length === 0 && (
          <div style={{ fontSize: 12, color: '#a09882', padding: '12px 0' }}>
            No campaign slots this week. New offers appear each Monday.
          </div>
        )}
        <motion.div variants={staggerContainer} initial="initial" animate="animate" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {slots.map(template => {
            const techOk = !template.techRequired || state.unlockedTech?.includes(template.techRequired);
            const canAfford = state.cash >= template.cost;
            const maxCampaigns = campaigns.length < (mkt.maxCampaignSlots || 3);
            return (
              <CampaignCard
                key={template.id}
                template={template}
                canLaunch={techOk && maxCampaigns}
                canAfford={canAfford}
                onLaunch={() => handleLaunch(template.id)}
              />
            );
          })}
        </motion.div>

        {/* ── Active Campaigns ── */}
        {campaigns.length > 0 && (
          <>
            <SectionHead accent={FOREST}>Active Campaigns ({campaigns.length}/{mkt.maxCampaignSlots || 3})</SectionHead>
            {campaigns.map(c => (
              <ActiveCampaignRow key={c.id} campaign={c} onCancel={() => handleCancel(c.id)} />
            ))}
          </>
        )}

        {/* ── PR Crises ── */}
        {crises.length > 0 && (
          <>
            <SectionHead accent={DANGER}>PR Crises</SectionHead>
            {crises.map(crisis => (
              <div key={crisis.id} style={{
                background: '#1a0a0a', border: `1px solid ${DANGER}`, borderRadius: 6,
                padding: '12px 14px', marginBottom: 8,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#ef4444', marginBottom: 4 }}>
                  {crisis.description || 'PR Crisis'}
                </div>
                <div style={{ fontSize: 11, color: '#a09882', marginBottom: 8 }}>
                  Severity: {'!'.repeat(crisis.severity || 1)} | Expires day {crisis.expiresOnDay || '?'}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePRResponse(crisis.id, 'apologize')}>
                    Apologize
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePRResponse(crisis.id, 'deny')}>
                    Deny
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{ color: '#a09882' }} onClick={() => handlePRResponse(crisis.id, 'ignore')}>
                    Ignore
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ═══ RIGHT — Sidebar ═══ */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto', borderLeft: '1px solid var(--border)', paddingLeft: 14 }}>

        {/* ── Tour Scheduler ── */}
        <SectionHead>Facility Tours</SectionHead>
        <div style={{ fontSize: 11, color: '#a09882', marginBottom: 8, lineHeight: 1.5 }}>
          Schedule a guided tour of your operation. Visitors arrive in 3 days and judge cleanliness, equipment, and pest control.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
          <span style={{ color: 'var(--text-muted)' }}>This month</span>
          <span>{tourCountThisMonth}/{MAX_TOURS_PER_MONTH}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 8 }}>
          <span style={{ color: 'var(--text-muted)' }}>Fee</span>
          <span style={{ color: AMBER }}>${TOUR_FEE}</span>
        </div>
        <button
          className="btn btn-primary btn-sm"
          style={{ width: '100%', background: canScheduleTour ? RUST : undefined, borderColor: canScheduleTour ? AMBER : undefined, marginBottom: 10 }}
          disabled={!canScheduleTour}
          onClick={handleScheduleTour}
        >
          {!hasD4 ? 'Requires D4' : tourCountThisMonth >= MAX_TOURS_PER_MONTH ? 'Monthly cap reached' : state.cash < TOUR_FEE ? 'Not enough cash' : 'Schedule Tour'}
        </button>

        {tours.length > 0 && tours.map(t => (
          <div key={t.id} style={{ background: '#1c1a16', borderRadius: 6, padding: '8px 10px', marginBottom: 6, border: `1px solid ${AMBER}22` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
              <span>Tour</span>
              <span style={{ color: '#a09882' }}>Day {t.scheduledDay}</span>
            </div>
            <button className="btn btn-secondary btn-sm" style={{ width: '100%', fontSize: 10 }} onClick={() => handleCancelTour(t.id)}>
              Cancel (50% refund)
            </button>
          </div>
        ))}

        {/* ── Packaging Lane ── */}
        <SectionHead>Packaging</SectionHead>
        <div style={{ fontSize: 11, color: '#a09882', marginBottom: 8, lineHeight: 1.5 }}>
          Upgrade packaging tiers on processed inventory to boost retail value. Premium packaging commands higher prices.
        </div>
        <button
          className="btn btn-secondary btn-sm"
          style={{ width: '100%' }}
          onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'market' } })}
        >
          Go to Market →
        </button>

        {/* ── Campaign History ── */}
        {history.length > 0 && (
          <>
            <SectionHead accent="#706856">Recent Campaigns</SectionHead>
            {history.map((h, i) => (
              <div key={i} style={{ fontSize: 11, padding: '4px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#a09882' }}>{h.name || h.templateId}</span>
                <span style={{ color: h.result === 'backlash' ? DANGER : FOREST, fontWeight: 600, fontSize: 10 }}>
                  {h.result === 'backlash' ? 'BACKLASH' : 'SUCCESS'}
                </span>
              </div>
            ))}
          </>
        )}

        {/* ── Back to facility ── */}
        <div style={{ marginTop: 'auto', paddingTop: 16 }}>
          <button
            className="btn btn-secondary"
            style={{ width: '100%' }}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'facility' } })}
          >
            Back to Facility
          </button>
        </div>
      </div>
    </div>
  );
}
